import {
  callAiCommandStream,
  callAiCompress,
  callAiMicroStep,
  callAiSchedule,
  type CommandInput,
  type CommandOutput,
  type CompressInput,
  type CompressOutput,
  type MicroStepInput,
  type MicroStepOutput,
  type ScheduleInput,
  type ScheduleOutput,
} from '@/lib/aiClient';
import {
  callStoreGoogleRefreshToken,
  callExchangeGoogleCode,
  callReconcileGoogleCalendar,
  callProbeGoogleCalendar,
  callRevokeGoogleRefreshToken,
  type ExchangeGoogleCodeInput,
  type ExchangeGoogleCodeOutput,
  type MintedBy,
} from '@/lib/calendarClient';

export type BraindayErrorCode =
  | 'timeout'
  | 'offline'
  | 'rate-limit'
  | 'app-check'
  | 'server'
  | 'token-expired'
  | 'not-connected'
  | 'unknown';

export class BraindayError extends Error {
  constructor(
    public readonly code: BraindayErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'BraindayError';
  }
}

const TIMEOUT_MS = 35_000;

interface FirebaseFnErrorLike {
  code?: string;
  message?: string;
}

function mapError(err: unknown): BraindayError {
  const e = err as FirebaseFnErrorLike;
  const rawCode = e?.code ?? '';
  const code = rawCode.replace(/^functions\//, '');
  const msg = e?.message ?? String(err);
  if (code === 'unavailable') return new BraindayError('offline', msg);
  if (code === 'deadline-exceeded') return new BraindayError('timeout', msg);
  if (code === 'resource-exhausted') return new BraindayError('rate-limit', msg);
  if (code === 'unauthenticated') return new BraindayError('app-check', msg);
  if (code === 'internal') return new BraindayError('server', msg);
  if (code === 'failed-precondition') {
    const detailsCode =
      (e as FirebaseFnErrorLike & { details?: { code?: string } }).details?.code ?? '';
    if (detailsCode === 'TOKEN_EXPIRED') return new BraindayError('token-expired', msg);
    if (detailsCode === 'NOT_CONNECTED') return new BraindayError('not-connected', msg);
  }
  return new BraindayError('unknown', msg);
}

// token-expired and not-connected are deterministic server verdicts — an
// immediate retry can only repeat them (and doubles latency before the UI
// can show the Reconnect prompt).
const NO_RETRY: BraindayErrorCode[] = ['rate-limit', 'app-check', 'token-expired', 'not-connected'];

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new BraindayError('timeout', `Timed out after ${ms}ms`)),
      ms,
    );
    promise.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (e: unknown) => {
        clearTimeout(timer);
        reject(e instanceof Error ? e : new BraindayError('unknown', String(e)));
      },
    );
  });
}

async function withRetryAndTimeout<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await withTimeout(fn(), TIMEOUT_MS);
  } catch (err) {
    const mapped = err instanceof BraindayError ? err : mapError(err);
    if (NO_RETRY.includes(mapped.code) || mapped.code === 'timeout') throw mapped;
    try {
      return await withTimeout(fn(), TIMEOUT_MS);
    } catch (err2) {
      throw err2 instanceof BraindayError ? err2 : mapError(err2);
    }
  }
}

export const BraindayAPI = {
  schedule: (input: ScheduleInput): Promise<ScheduleOutput> =>
    withRetryAndTimeout(() => callAiSchedule(input)),
  microStep: (input: MicroStepInput): Promise<MicroStepOutput> =>
    withRetryAndTimeout(() => callAiMicroStep(input)),
  compress: (input: CompressInput): Promise<CompressOutput> =>
    withRetryAndTimeout(() => callAiCompress(input)),
  // NOTE: onChunk may still fire after a timeout rejection because Firebase
  // streams cannot be aborted mid-flight. Callers should guard against late
  // chunks (e.g., by ignoring them once an error state is set).
  command: (
    input: CommandInput,
    onChunk: (text: string) => void,
    opts?: { signal?: AbortSignal },
  ): Promise<CommandOutput> => withRetryAndTimeout(() => callAiCommandStream(input, onChunk, opts)),

  // ----- Phase 11: Google Calendar -----
  storeGoogleRefresh: (refreshToken: string, mintedBy: MintedBy) =>
    withRetryAndTimeout(() => callStoreGoogleRefreshToken(refreshToken, mintedBy)),
  exchangeGoogleCode: (input: ExchangeGoogleCodeInput): Promise<ExchangeGoogleCodeOutput> =>
    withRetryAndTimeout(() => callExchangeGoogleCode(input)),
  reconcileCalendar: (input?: { rangeStartMs?: number; rangeEndMs?: number }) =>
    withRetryAndTimeout(() => callReconcileGoogleCalendar(input)),
  probeCalendar: () => withRetryAndTimeout(() => callProbeGoogleCalendar()),
  revokeGoogleRefresh: () => withRetryAndTimeout(() => callRevokeGoogleRefreshToken()),
};
